import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProjectCommentAnchorSummaryDto, ProjectCommentTargetKind } from '@paladin-monorepo/common';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ProjectEntityComments } from '@/components/project/ProjectEntityComments';
import {
  getProjectCommentSeenAt,
  setProjectCommentSeenAt,
  PROJECT_COMMENT_SEEN_EVENT,
} from '@/lib/project-comment-seen';
import { MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils';

function useUnreadForAnchor(
  projectId: string,
  targetKind: ProjectCommentTargetKind,
  targetId: string,
  summary: ProjectCommentAnchorSummaryDto | null | undefined,
): boolean {
  const [, setTick] = useState(0);
  const bump = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key?.startsWith('paladin.commentSeen.v1')) {
        bump();
      }
    };
    const onSeen = () => bump();
    window.addEventListener('storage', onStorage);
    window.addEventListener(PROJECT_COMMENT_SEEN_EVENT, onSeen);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(PROJECT_COMMENT_SEEN_EVENT, onSeen);
    };
  }, [bump]);

  const lastAt = summary?.lastCommentAt;
  const total = summary?.totalCount ?? 0;
  if (!lastAt || total <= 0) {
    return false;
  }
  const seen = getProjectCommentSeenAt(projectId, targetKind, targetId);
  if (!seen) {
    return true;
  }
  return new Date(lastAt).getTime() > new Date(seen).getTime();
}

export function ProjectCommentsDialogTrigger({
  projectId,
  targetKind,
  targetId,
  summary,
  autoOpen = false,
  className,
}: {
  projectId: string;
  targetKind: ProjectCommentTargetKind;
  targetId: string;
  summary?: ProjectCommentAnchorSummaryDto | null;
  autoOpen?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const didAutoOpen = useRef(false);
  const summaryForKey = summary ?? null;
  const unread = useUnreadForAnchor(projectId, targetKind, targetId, summaryForKey);

  const markOpened = useCallback(() => {
    const at = summary?.lastCommentAt ?? new Date().toISOString();
    setProjectCommentSeenAt(projectId, targetKind, targetId, at);
  }, [projectId, targetKind, targetId, summary?.lastCommentAt]);

  useEffect(() => {
    if (autoOpen && !didAutoOpen.current) {
      didAutoOpen.current = true;
      setOpen(true);
      markOpened();
    }
  }, [autoOpen, markOpened]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          markOpened();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn('relative h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground', className)}
          aria-label={t('workspace.comments.openThread')}
        >
          <MessageSquare className="h-4 w-4" aria-hidden />
          {unread && (
            <span
              className="absolute right-0.5 top-0.5 h-2 w-2 rounded-full bg-primary shadow-sm ring-2 ring-card"
              aria-hidden
            />
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="flex max-h-[min(85vh,720px)] w-[calc(100%-2rem)] max-w-lg flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="border-b border-border px-4 py-3 shrink-0">
          <DialogTitle>{t('workspace.comments.dialogTitle')}</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          <ProjectEntityComments
            projectId={projectId}
            targetKind={targetKind}
            targetId={targetId}
            layout="dialog"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
